import React, { useState } from 'react';
import { X, Loader2, Building2, Tag, Phone } from 'lucide-react';
import { paraSlug, usuarioPadraoDoSlug } from '@nyroh/contracts';
import { ClientEntity, ClientData } from '../types';
import { db } from '../services/db';
import { toast } from 'sonner';

interface ManualClientModalProps {
  onClose: () => void;
  onSave: () => void;
}

export const ManualClientModal: React.FC<ManualClientModalProps> = ({ onClose, onSave }) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [phone, setPhone] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('O nome do negócio é obrigatório.');
      return;
    }

    setIsSaving(true);
    try {
      // Pela regra do contrato, a mesma que o servidor usa e que o `slugSchema`
      // valida. A versao local aqui nao tinha limite de tamanho.
      const baseSlug = paraSlug(name) || `cliente-${Date.now()}`;

      /*
       * O usuario sai de `usuarioPadraoDoSlug`, do contrato — a mesma funcao que
       * o servidor usa ao criar a credencial. Aqui era
       * `baseSlug.replace(/-/g,'').substring(0, 15)`, uma terceira regra: para
       * qualquer slug com mais de 15 caracteres o painel gravava um usuario que
       * o login jamais casaria. A conta existia e o dono nao conseguia entrar.
       *
       * E o PIN saiu daqui de vez. Era
       * `Math.floor(1000 + Math.random() * 9000)` — gerador nao criptografico,
       * no NAVEGADOR, 9 mil combinacoes, viajando em texto puro. Quem emite
       * credencial e o servidor: `clientes.criar` cria a do portal com a senha
       * padrao e marca a troca como obrigatoria.
       */
      const username = usuarioPadraoDoSlug(baseSlug);

      const htmlContent = `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${name}</title>
  <style>
    body { font-family: system-ui, -apple-system, sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; margin: 0; background: #f8fafc; color: #334155; }
    .card { background: white; padding: 3rem; border-radius: 1rem; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); text-align: center; max-width: 500px; width: 90%; }
    h1 { font-size: 2rem; margin-bottom: 1rem; color: #0f172a; }
    p { line-height: 1.6; color: #64748b; margin-bottom: 2rem; }
    .badge { display: inline-block; padding: 0.5rem 1rem; background: #f1f5f9; border-radius: 2rem; font-size: 0.875rem; font-weight: 500; color: #475569; margin-bottom: 1.5rem; }
  </style>
</head>
<body>
  <div class="card">
    <div class="badge">${category || 'Novo Site'}</div>
    <h1>${name}</h1>
    <p>Este site está em construção. Em breve teremos novidades por aqui!</p>
    ${phone ? `<p style="font-weight: 500; color: #0f172a;">Contato: ${phone}</p>` : ''}
  </div>
</body>
</html>`;

      const newClientData: ClientData = {
        id: Date.now().toString(36) + Math.random().toString(36).substring(2),
        campaignId: '',
        targetId: '',
        name: name.trim(),
        address: 'Endereço não informado',
        rating: 5,
        reviewCount: 0,
        website: null,
        categories: category ? [category.trim()] : [],
        topReviews: [],
        summary: 'Site criado manualmente. Adicione mais informações no código ou no painel.',
        blueprint: [],
        placeUri: '',
        phoneNumber: phone.trim(),
        openingHours: '',
        businessStatus: 'Operacional',
        priceLevel: null,
        amenities: [],
        photoAnalysis: '',
        seoKeywords: [],
        createdAt: new Date().toISOString(),
        username,
      };

      const newClient: ClientEntity = {
        id: newClientData.id,
        data: newClientData,
        htmlContent,
        slug: baseSlug,
        status: 'pending'
      };

      await db.saveClients([newClient]);
      toast.success('Cliente cadastrado com sucesso!');
      onSave();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao cadastrar cliente.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h3 className="text-lg font-semibold text-gray-900">Novo Cliente Manual</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome do Negócio *</label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input 
                type="text" 
                required
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Pizzaria do João"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-black focus:border-black outline-none transition-all text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Categoria / Nicho</label>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input 
                type="text" 
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Ex: Alimentação, Advogado..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-black focus:border-black outline-none transition-all text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefone / WhatsApp</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input 
                type="text" 
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Ex: (11) 99999-9999"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-black focus:border-black outline-none transition-all text-sm"
              />
            </div>
          </div>

          <div className="pt-4 flex gap-3">
            <button 
              type="button" 
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              disabled={isSaving || !name.trim()}
              className="flex-1 px-4 py-2.5 bg-black text-white rounded-lg font-medium hover:bg-gray-900 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? <Loader2 size={18} className="animate-spin" /> : null}
              {isSaving ? 'Salvando...' : 'Criar Cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
